import { Alert, AlertTitle, Grow, Typography, useTheme } from '@mui/material';
import { ErrorIcon, MdInfo, MdWarning, SuccessIcon } from '@teamworksdev/icons';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { TOAST_REMOVE_DELAY, useToast } from '../../hooks/useToast';

export type ToastProps = {
  id: string;
  title?: string;
  description?: string;
  action?: ReactNode;
  duration?: number;
  severity?: 'success' | 'info' | 'warning' | 'error';
  variant?: 'filled' | 'outlined';
  onClose?: () => void;
};

export function Toast({
  toast: {
    id,
    onClose,
    duration,
    variant = 'filled',
    title,
    description,
    action,
    severity,
  },
}: {
  toast: ToastProps;
}) {
  const {
    palette: {
      greyscaleDark,
      success,
      warning,
      info,
      error,
      highEmphasisTextDark,
      highEmphasisTextLight,
      mediumEmphasisTextDark,
    },
  } = useTheme();

  const [open, setOpen] = useState(true);

  const { dismiss } = useToast();

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      dismiss(id);
    }, TOAST_REMOVE_DELAY);
  }, [dismiss, id]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    close();
  };

  useEffect(() => {
    if (duration !== 0) {
      setTimeout(() => {
        close();
      }, duration || 3000);
    }
  }, [close, duration]);

  return (
    <Grow in={open} timeout={TOAST_REMOVE_DELAY}>
      <Alert
        key={id}
        severity={severity || 'success'}
        onClose={() => handleClose()}
        variant={variant}
        sx={{
          backgroundColor:
            variant === 'filled' ? greyscaleDark[800] : 'background.paper',
          minWidth: 280,
          width: { xs: 1, md: 'auto' },
        }}
        iconMapping={{
          success: <SuccessIcon color={success.main} />,
          warning: <MdWarning color={warning.main} />,
          info: <MdInfo color={info.main} />,
          error: <ErrorIcon color={error.main} />,
        }}
      >
        {title && (
          <AlertTitle
            sx={{
              color:
                variant === 'filled'
                  ? highEmphasisTextDark
                  : highEmphasisTextLight,
            }}
          >
            {title}
          </AlertTitle>
        )}
        <Typography
          color={
            variant === 'filled'
              ? mediumEmphasisTextDark
              : highEmphasisTextLight
          }
          variant="body2"
        >
          {description}
        </Typography>
        {action}
      </Alert>
    </Grow>
  );
}
